/**
 * Impact director — the "game feel" layer.
 *
 * Owns two effects that work by modulating time, so they have to live outside
 * the entities that trigger them:
 *
 *   hitStop(ms)   Freezes the simulation for a few frames on a heavy landing.
 *                 This is what makes a cannon shot feel like it weighs
 *                 something: the world stops, then snaps back.
 *   slowMo(f, ms) Scales time down (not to zero) — used for boss deaths.
 *
 * The camera itself is deliberately never moved: shakes and zoom kicks read as
 * distracting in a game where you are reading a whole board at once.
 *
 * GameScene multiplies its delta by timeScale each frame, so every system
 * already driven by the scaled delta inherits hit-stop for free.
 *
 * Everything is budgeted: hit-stop is capped and rate-limited so a screen full
 * of cannons can't stutter the game into a slideshow.
 */
import Phaser from "phaser";

/** Never freeze longer than this in one go. */
const MAX_STOP_MS = 90;
/** Minimum gap between hit-stops, so dense fire can't chain-freeze the game. */
const STOP_COOLDOWN_MS = 140;

class ImpactSystem {
  private scene?: Phaser.Scene;

  private stopMs = 0;
  private stopCooldownMs = 0;

  private slowFactor = 1;
  private slowMs = 0;

  /** Frame time multiplier the scene should apply. 0 while frozen. */
  get timeScale(): number {
    if (this.stopMs > 0) return 0;
    return this.slowMs > 0 ? this.slowFactor : 1;
  }

  attach(scene: Phaser.Scene): void {
    this.scene = scene;
    this.stopMs = 0;
    this.stopCooldownMs = 0;
    this.slowMs = 0;
    this.slowFactor = 1;
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.detach());
  }

  detach(): void {
    this.scene = undefined;
    this.stopMs = 0;
    this.slowMs = 0;
  }

  /**
   * Freeze the simulation briefly. Rate-limited: a request arriving during the
   * cooldown is dropped rather than queued, so heavy fire stays smooth.
   */
  hitStop(ms: number): void {
    if (!this.scene) return;
    if (this.stopCooldownMs > 0) return;
    this.stopMs = Math.min(ms, MAX_STOP_MS);
    this.stopCooldownMs = STOP_COOLDOWN_MS;
  }

  /** Scale time down for a while — `factor` 0.25 = quarter speed. */
  slowMo(factor: number, ms: number): void {
    if (!this.scene) return;
    this.slowFactor = Phaser.Math.Clamp(factor, 0.05, 1);
    this.slowMs = ms;
  }

  /**
   * Advance timers. Pass the REAL delta — the impact director is what defines
   * scaled time, so it can't be driven by its own output.
   */
  update(deltaMs: number): void {
    if (!this.scene) return;

    if (this.stopMs > 0) this.stopMs -= deltaMs;
    if (this.stopCooldownMs > 0) this.stopCooldownMs -= deltaMs;
    if (this.slowMs > 0) {
      this.slowMs -= deltaMs;
      if (this.slowMs <= 0) this.slowFactor = 1;
    }
  }
}

export const impact = new ImpactSystem();
